import { WallShape } from "../types";

interface Point {
  x: number;
  y: number;
}

interface WallSide {
  start: Point;
  end: Point;
}

const EPSILON = 5;

const pointsNear = (a: Point, b: Point): boolean =>
  Math.abs(a.x - b.x) < EPSILON && Math.abs(a.y - b.y) < EPSILON;

/**
 * Check if a wall approximately covers a given side of the bounding rectangle.
 * A wall covers a side if both its endpoints are near the side's endpoints
 * (in either order).
 */
const wallCoversSide = (wall: WallShape, side: WallSide): boolean => {
  const wStart = { x: wall.x, y: wall.y };
  const wEnd = { x: wall.x2, y: wall.y2 };

  return (
    (pointsNear(wStart, side.start) && pointsNear(wEnd, side.end)) ||
    (pointsNear(wStart, side.end) && pointsNear(wEnd, side.start))
  );
};

/**
 * Given a set of selected walls, compute the axis-aligned bounding rectangle
 * of all their endpoints and return any missing sides needed to close the rectangle.
 *
 * Returns an array of missing wall sides (start/end pairs), or null if
 * fewer than 2 walls are provided or all sides are already covered.
 */
export const findMissingRectWalls = (
  selectedWalls: WallShape[]
): WallSide[] | null => {
  if (selectedWalls.length < 2) return null;

  // Gather all endpoints
  const points: Point[] = [];
  for (const wall of selectedWalls) {
    points.push({ x: wall.x, y: wall.y });
    points.push({ x: wall.x2, y: wall.y2 });
  }

  // Compute bounding box
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  // Bounding box too small — not a meaningful rectangle
  if (maxX - minX < EPSILON || maxY - minY < EPSILON) return null;

  // Define the 4 sides of the bounding rectangle
  const topLeft = { x: minX, y: minY };
  const topRight = { x: maxX, y: minY };
  const bottomRight = { x: maxX, y: maxY };
  const bottomLeft = { x: minX, y: maxY };

  const sides: WallSide[] = [
    { start: topLeft, end: topRight },
    { start: topRight, end: bottomRight },
    { start: bottomRight, end: bottomLeft },
    { start: bottomLeft, end: topLeft }
  ];

  // Find which sides are missing
  const missingSides = sides.filter(
    (side) => !selectedWalls.some((wall) => wallCoversSide(wall, side))
  );

  if (missingSides.length === 0 || missingSides.length === 4) return null;

  return missingSides;
};
